import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Tooltip
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import ViewColumnRounded from "@mui/icons-material/ViewColumnRounded";

import type { Bot, BrokerAccount } from "@/data/mock-data";
import type { Order, OrderStatus } from "@/data/mock-orders";
import { formatUSD } from "@/lib/formatters";

// ---- Column schema --------------------------------------------------------

/**
 * Column ids ordered left-to-right. The ordering doubles as the render
 * order — we don't support user-reordering in this pass. Columns split
 * into two buckets:
 *  • `defaultVisible` — on from the start, covers Alpaca's baseline.
 *  • `optional` — off by default, surfaced via the column-chooser menu.
 *
 * Keep the two sets disjoint; the menu would misbehave if a column
 * appeared in both lists.
 */
type ColumnId =
  | "asset"
  | "side"
  | "orderType"
  | "filledQty"
  | "avgFillPrice"
  | "total"
  | "realized"
  | "status"
  | "submittedAt"
  | "account"
  | "limitPrice"
  | "stopPrice"
  | "notional"
  | "source"
  | "strategy"
  | "filledAt"
  | "canceledAt";

interface ColumnSpec {
  id: ColumnId;
  label: string;
  /** Right-align numeric columns — scans better than default left. */
  align?: "left" | "right";
  /** Collapse to content width so Side/Status stay narrow. */
  width?: number | string;
}

const COLUMNS: Record<ColumnId, ColumnSpec> = {
  asset: { id: "asset", label: "Asset", align: "left", width: 180 },
  side: { id: "side", label: "Side", align: "left", width: 68 },
  orderType: { id: "orderType", label: "Order type", align: "left", width: 120 },
  filledQty: { id: "filledQty", label: "Filled qty", align: "right", width: 100 },
  avgFillPrice: { id: "avgFillPrice", label: "Avg. fill", align: "right", width: 110 },
  total: { id: "total", label: "Total", align: "right", width: 120 },
  realized: { id: "realized", label: "Realized", align: "right", width: 130 },
  status: { id: "status", label: "Status", align: "left", width: 100 },
  submittedAt: { id: "submittedAt", label: "Submitted", align: "left", width: 150 },
  // --- optional ---
  account: { id: "account", label: "Account", align: "left", width: 140 },
  limitPrice: { id: "limitPrice", label: "Limit", align: "right", width: 100 },
  stopPrice: { id: "stopPrice", label: "Stop", align: "right", width: 100 },
  notional: { id: "notional", label: "Notional", align: "right", width: 110 },
  source: { id: "source", label: "Source", align: "left", width: 90 },
  strategy: { id: "strategy", label: "Strategy", align: "left", width: 140 },
  filledAt: { id: "filledAt", label: "Filled at", align: "left", width: 150 },
  canceledAt: { id: "canceledAt", label: "Canceled at", align: "left", width: 150 }
};

const DEFAULT_VISIBLE: ColumnId[] = [
  "asset",
  "side",
  "orderType",
  "filledQty",
  "avgFillPrice",
  "total",
  "status",
  "submittedAt"
];

const OPTIONAL_COLUMNS: ColumnId[] = [
  "account",
  // "realized" is optional rather than default — only a subset of orders
  // are closing trades (sell on a long, buy on a short cover), so the
  // column renders "—" on ~half the rows. Advanced users who want to
  // scan realized P&L across history can flick it on from the chooser
  // menu; casual viewers keep the leaner default column set.
  "realized",
  "limitPrice",
  "stopPrice",
  "notional",
  "source",
  "strategy",
  "filledAt",
  "canceledAt"
];

// ---- Component ------------------------------------------------------------

interface OrdersAdvancedViewProps {
  orders: Order[];
  bots: Bot[];
  /**
   * Full account list — used by the (optional) Account column to resolve
   * `order.accountId` into a human name. Required when `showAccount` is
   * set; otherwise the column will fall back to the raw id.
   */
  accounts?: BrokerAccount[];
  /**
   * When true, flip the Account column on by default. The column is still
   * toggleable via the column-chooser menu either way — this just decides
   * the initial state. We default it on in aggregate "All Live Accounts"
   * view where orders span multiple accounts and the owning account is
   * critical context.
   */
  showAccount?: boolean;
  /**
   * Live ticker → company-name overrides, sourced from the same
   * /api/market/quotes pipeline the chart uses. When a row's symbol
   * is in this map, its long name takes priority over `order.assetName`
   * (which itself has the static-table fallback baked in via the
   * adapter). Caller is `OrdersPanel` in `AccountsPageV2`.
   */
  tickerNames?: Record<string, string>;
}

/**
 * Alpaca-style dense data table.
 *
 * Mobile behaviour:
 *  • Parent's section container has already clamped the inner width
 *    to the shell. We render a horizontally-scrollable `overflow-x`
 *    wrapper so the table can exceed that width comfortably.
 *  • First column (Asset) sticks during horizontal scroll — the
 *    ticker is the row identifier, so keeping it in view while the
 *    user swipes to expose extra columns preserves context. Both
 *    the header and body cells get `position: sticky; left: 0`.
 *
 * Desktop: at >= md the container fits the viewport width, so there's
 * no scroll in practice — the sticky column is still sticky but it
 * never moves because the content doesn't overflow.
 */
export function OrdersAdvancedView({
  orders,
  bots,
  accounts,
  showAccount,
  tickerNames
}: OrdersAdvancedViewProps) {
  // Seed visible columns. If the caller asked for the Account column
  // (aggregate mode), append it to the default set so it shows right
  // after Submitted without disturbing the Alpaca baseline. Users can
  // still toggle it off via the column chooser.
  const [visible, setVisible] = useState<ColumnId[]>(
    showAccount ? [...DEFAULT_VISIBLE, "account"] : DEFAULT_VISIBLE
  );
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const toggleColumn = (id: ColumnId) => {
    // Default columns are always on — they form the Alpaca baseline
    // and hiding e.g. `Total` would turn the view into something
    // unrecognizable. Prevent toggling those off.
    if (DEFAULT_VISIBLE.includes(id)) return;
    setVisible((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  if (orders.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          textAlign: "center",
          color: "text.secondary"
        }}
      >
        <Typography variant="body2">No orders match your filters.</Typography>
      </Box>
    );
  }

  // Render columns left-to-right in the same order as `COLUMNS` keys,
  // filtering to the currently-visible set. This keeps optional
  // columns docked in their canonical slot rather than tacked on at
  // the end when toggled on.
  const renderedCols: ColumnSpec[] = (Object.keys(COLUMNS) as ColumnId[])
    .filter((id) => visible.includes(id))
    .map((id) => COLUMNS[id]);

  return (
    <Box sx={{ pt: 1 }}>
      {/* Header row — row count + column chooser. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          {orders.length} {orders.length === 1 ? "order" : "orders"}
        </Typography>
        <Tooltip title="Choose columns">
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label="Choose columns"
          >
            <ViewColumnRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* 2026-04-26 Sergey: dropped the bordered "card" treatment + extend
       *  edge-to-edge with negative horizontal margin (cancels the
       *  enclosing page's `px: 2`). The page's outer chrome already
       *  carries the visual frame; another nested border just stole
       *  precious horizontal space on mobile. */}
      <Box
        sx={{
          overflowX: "auto",
          // To pin the table header at the top, this wrapper must be
          // the Y scroll container — sticky `<th>` elements inside
          // glue to its top edge. Without an explicit maxHeight the
          // wrapper grows to fit content and never actually scrolls
          // internally, defeating the sticky. Capping it at
          // `calc(100vh - 200px)` leaves room above for the sticky
          // account selector + chart and below for the bottom nav,
          // and turns the orders area into a self-contained scroll
          // surface — exactly the "scrolling moves only the order
          // rows once you reach the table" UX Sergey asked for
          // (2026-04-26).
          overflowY: "auto",
          maxHeight: "calc(100vh - 200px)",
          mx: -2,
          "&::-webkit-scrollbar": { width: 6, height: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: (t) => alpha(t.palette.text.secondary, 0.3),
            borderRadius: 3
          }
        }}
      >
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {renderedCols.map((col, i) => (
                <TableCell
                  key={col.id}
                  align={col.align ?? "left"}
                  sx={{
                    fontWeight: 600,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    fontSize: 12,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    bgcolor: "background.paper",
                    // Spread first (carries left:0 + z-index:2 + box-
                    // shadow for the first column), then layer the
                    // header-row pinning ON TOP so our values win where
                    // they overlap (top: 0 and the bumped z-index).
                    ...(i === 0 && stickyFirstCellSx),
                    // Pin the header to the top of the page's scroll
                    // container so column titles stay visible while
                    // the user scrolls long order lists. Sergey
                    // 2026-04-26.
                    position: "sticky",
                    top: 0,
                    // Stack above body rows. The corner cell needs to
                    // beat both the body's row-level sticky first cell
                    // (z-index 2) AND the other header cells when
                    // both axes are scrolling, hence z 3.
                    zIndex: i === 0 ? 3 : 2,
                    // Hairline at the bottom so header reads as
                    // separated from the rows below when pinned.
                    borderBottom: (t) => `1px solid ${t.palette.divider}`
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((o) => (
              <TableRow
                key={o.id}
                hover
                sx={{
                  "&:last-of-type td": { borderBottom: 0 }
                }}
              >
                {renderedCols.map((col, i) => (
                  <TableCell
                    key={col.id}
                    align={col.align ?? "left"}
                    sx={{
                      whiteSpace: "nowrap",
                      fontFeatureSettings:
                        col.align === "right" ? '"tnum" 1' : undefined,
                      bgcolor: "background.paper",
                      ...(i === 0 && stickyFirstCellSx)
                    }}
                  >
                    {renderCell(col.id, o, bots, accounts, tickerNames)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Column chooser menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        {DEFAULT_VISIBLE.map((id) => (
          <MenuItem key={id} disabled dense>
            <Checkbox
              edge="start"
              size="small"
              checked
              disabled
              tabIndex={-1}
              disableRipple
            />
            <ListItemText
              primary={COLUMNS[id].label}
              secondary="Always shown"
              primaryTypographyProps={{ variant: "body2" }}
              secondaryTypographyProps={{
                variant: "caption",
                color: "text.secondary"
              }}
            />
          </MenuItem>
        ))}
        {OPTIONAL_COLUMNS.map((id) => (
          <MenuItem key={id} dense onClick={() => toggleColumn(id)}>
            <Checkbox
              edge="start"
              size="small"
              checked={visible.includes(id)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText
              primary={COLUMNS[id].label}
              primaryTypographyProps={{ variant: "body2" }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}

// ---- Sticky first-column sx ----------------------------------------------

/**
 * Shared sx for the Asset column's sticky behaviour. MUI `TableCell`s
 * are `display: table-cell`, not flex/grid, so plain `position: sticky`
 * works as long as the cell has a solid background (to hide the rows
 * scrolling beneath it) and a high enough z-index to stack above
 * neighbouring cells.
 */
const stickyFirstCellSx = {
  position: "sticky" as const,
  left: 0,
  zIndex: 2,
  // Hairline to mark the sticky edge when the table has scrolled.
  boxShadow: (t: Theme) => `inset -1px 0 0 0 ${t.palette.divider}`
};

// ---- Cell renderers -------------------------------------------------------

function renderCell(
  id: ColumnId,
  o: Order,
  bots: Bot[],
  accounts?: BrokerAccount[],
  tickerNames?: Record<string, string>
): React.ReactNode {
  switch (id) {
    case "asset": {
      // Live name from /api/market/quotes wins over the adapter's
      // stamped assetName (which already has the static-table
      // fallback). Only override when the live name is BOTH present
      // AND meaningfully different from the symbol — guards against
      // an upstream that returned the symbol as the "name".
      const liveName = tickerNames?.[o.asset.toUpperCase()];
      const display =
        liveName && liveName.toUpperCase() !== o.asset.toUpperCase()
          ? liveName
          : o.assetName;
      return (
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, lineHeight: 1.2 }}
          >
            {o.asset}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              maxWidth: 160,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            {display}
          </Typography>
        </Box>
      );
    }
    case "side":
      return (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            textTransform: "uppercase",
            color: o.side === "buy" ? "success.main" : "error.main"
          }}
        >
          {o.side}
        </Typography>
      );
    case "orderType":
      return formatOrderType(o.orderType);
    case "filledQty":
      return formatQty(o.filledQty);
    case "avgFillPrice":
      return formatUSD(o.avgFillPrice);
    case "total":
      return formatUSD(o.totalAmount);
    case "realized":
      // Closing trades only — opens return `undefined` here. Render em-dash
      // so a toggled-on column stays structurally aligned even when most
      // rows don't carry a realized number.
      return o.realizedProfit !== undefined ? (
        <RealizedCell
          amount={o.realizedProfit}
          pct={o.realizedProfitPct}
        />
      ) : (
        "—"
      );
    case "status":
      return <StatusPill status={o.status} />;
    case "submittedAt":
      return formatTableTimestamp(o.submittedAt);
    case "account": {
      // Resolve accountId → account.name. Falls back to the raw id if
      // no account list was supplied or the id isn't found — shouldn't
      // happen in practice since the caller always ships `accounts`
      // alongside `showAccount`, but keeps the column renderable under
      // partial props instead of crashing.
      const a = accounts?.find((x) => x.id === o.accountId);
      return a?.name ?? o.accountId;
    }
    case "limitPrice":
      return o.limitPrice !== undefined ? formatUSD(o.limitPrice) : "—";
    case "stopPrice":
      return o.stopPrice !== undefined ? formatUSD(o.stopPrice) : "—";
    case "notional":
      return o.notional !== undefined ? formatUSD(o.notional) : "—";
    case "source":
      return capitalize(o.source);
    case "strategy": {
      // Prefer the strategy label directly; fall back to the bot name
      // so the column is never empty for a bot-placed order that
      // doesn't carry a strategy string.
      if (o.strategy) return o.strategy;
      if (o.botId) {
        const b = bots.find((x) => x.id === o.botId);
        return b?.name ?? "—";
      }
      return "—";
    }
    case "filledAt":
      return o.filledAt ? formatTableTimestamp(o.filledAt) : "—";
    case "canceledAt":
      return o.canceledAt ? formatTableTimestamp(o.canceledAt) : "—";
  }
}

// ---- Small helpers --------------------------------------------------------

function StatusPill({ status }: { status: OrderStatus }) {
  const palette = STATUS_PALETTE[status];
  return (
    <Chip
      label={palette.label}
      size="small"
      sx={(t: Theme) => ({
        bgcolor: alpha(palette.color(t), 0.14),
        color: palette.color(t),
        fontWeight: 600,
        borderRadius: 999,
        height: 20,
        "& .MuiChip-label": {
          px: 1,
          fontSize: 11,
          letterSpacing: 0.3
        }
      })}
    />
  );
}

const STATUS_PALETTE: Record<
  OrderStatus,
  { label: string; color: (t: Theme) => string }
> = {
  filled: { label: "Filled", color: (t) => t.palette.success.main },
  partial: { label: "Partial", color: (t) => t.palette.warning.main },
  open: { label: "Open", color: (t) => t.palette.info.main },
  canceled: { label: "Canceled", color: (t) => t.palette.text.secondary },
  expired: { label: "Expired", color: (t) => t.palette.text.secondary },
  replaced: { label: "Replaced", color: (t) => t.palette.info.main }
};

function formatOrderType(t: Order["orderType"]): string {
  // Dense-table variant — keeps labels short (one or two words). The
  // Simple view carries the verbose "Pending · releases ..." version
  // because the expand panel has room to breathe; the Advanced table
  // just uses the bare type so the column stays narrow.
  switch (t) {
    case "market":
      return "Market";
    case "limit":
      return "Limit";
    case "stop":
      return "Stop";
    case "stop_limit":
      return "Stop-limit";
    case "pending":
      return "Pending";
    case "scheduled":
      return "Scheduled";
    case "smart":
      return "Smart";
    case "correction":
      return "Correction";
  }
}

/**
 * Sign-coloured realized P&L cell for the advanced table. Compact by
 * design — stacks the $ delta with the % underneath in a small tnum
 * treatment so the two numbers share a visual column without bloating
 * the row height.
 */
function RealizedCell({ amount, pct }: { amount: number; pct?: number }) {
  const positive = amount >= 0;
  const color = (t: Theme) =>
    amount === 0
      ? t.palette.text.secondary
      : positive
        ? t.palette.success.main
        : t.palette.error.main;
  const sign = positive ? "+" : "−"; // U+2212 minus
  const abs = Math.abs(amount);

  return (
    <Box sx={{ lineHeight: 1.15, textAlign: "right" }}>
      <Typography
        variant="body2"
        sx={{ color, fontWeight: 600, fontFeatureSettings: '"tnum" 1' }}
      >
        {sign}
        {formatUSD(abs)}
      </Typography>
      {pct !== undefined && (
        <Typography
          variant="caption"
          sx={{ color, opacity: 0.85, fontFeatureSettings: '"tnum" 1' }}
        >
          {sign}
          {Math.abs(pct).toFixed(2)}%
        </Typography>
      )}
    </Box>
  );
}

function formatQty(n: number): string {
  if (Number.isInteger(n)) return n.toString();
  return n.toFixed(6).replace(/\.?0+$/, "");
}

function formatTableTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit"
  });
}

function capitalize(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}
